/**
 * Shared timezone-aware time formatters.
 * All times are formatted in the ACCOUNT timezone (workspace.timezone).
 * Never falls back to raw ISO strings on error — returns a safe placeholder.
 */

/**
 * Format an ISO timestamp as a human time string in the given timezone.
 * e.g. "2:31 PM"
 */
export function fmtTime(iso: string, timezone: string = 'America/Chicago'): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return '—';
  }
}

/**
 * Format an ISO timestamp as a smart relative label:
 * - Same day → time string (e.g. "2:31 PM")
 * - Yesterday → "Yesterday"
 * - Older → short date (e.g. "Jul 1")
 */
export function fmtRelative(iso: string, timezone: string = 'America/Chicago'): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const fmt = (date: Date) =>
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);

    const dStr = fmt(d);
    const nowStr = fmt(now);
    const yesterdayStr = fmt(new Date(now.getTime() - 86_400_000));

    if (dStr === nowStr) return fmtTime(iso, timezone);
    if (dStr === yesterdayStr) return 'Yesterday';

    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return '—';
  }
}
